module Shared.Admin.ManageApplications
open System
open Illy.SafeStack

type UserLevel =
    | OrganisationManager
    | DirectorateManager
    | LocationManager
    | TeamManager
    | ServiceWorker

module UserLevel =
    let toInt = 
        function 
        | OrganisationManager -> 1
        | DirectorateManager -> 2
        | LocationManager -> 3
        | TeamManager -> 4
        | ServiceWorker -> 5

    let ofInt = 
        function 
        | 1 -> OrganisationManager
        | 2 -> DirectorateManager
        | 3 -> LocationManager
        | 4 -> TeamManager
        | 5 -> ServiceWorker
        | x -> failwithf "Could not convert %d to %s" x typeof<UserLevel>.Name

    let toDisplayName = 
        function 
        | OrganisationManager -> "Organisation Manager"
        | DirectorateManager -> "Directorate Manager"
        | LocationManager -> "Location Manager"
        | TeamManager -> "Team Manager"
        | ServiceWorker -> "Service Worker"

    let toListOption x = {Id = toInt x; Value = toDisplayName x; Group=""}

type AttributeValueDTO = 
    | Checkbox of bool

type AttributeDTO = 
    {
        Name: string
        Label: string
        Value: AttributeValueDTO
    }

module AttributeDTO = 
    let create name label value = 
        {
            Name = name 
            Label = label 
            Value = value
        }

    let isSet (x: AttributeDTO) = match x.Value with Checkbox v -> v

    let toAttributeString attr = 
        let str = attr |> List.filter isSet |> List.map (fun x -> x.Label) |> String.concat ","
        if String.IsNullOrEmpty str then None 
        else Some str

type InitApplicationAccessDTO = 
    | HasAccess of bool 
    | UserLevel of UserLevel list * selectedUserLevel: UserLevel option 

type InitApplicationDTO = 
    {
        AssociationId: Guid
        AppId: int
        Image: string
        Name: string
        ApplicationAccess: InitApplicationAccessDTO
        Attributes: AttributeDTO list
    }

type InitUserDTO = 
    {
        Id: int
        Username: string 
        Forename: string 
        Surname: string 
        Applications: InitApplicationDTO list
    }

type SaveApplicationAccessDTO = 
    | HasAccess of bool 
    | UserLevel of selectedUserLevel: UserLevel option 

module SaveApplicationAccessDTO = 
    let ofInitDTO = 
        function 
        | InitApplicationAccessDTO.HasAccess v -> SaveApplicationAccessDTO.HasAccess v
        | InitApplicationAccessDTO.UserLevel (_,v) -> SaveApplicationAccessDTO.UserLevel v

type SaveApplicationDTO = 
    {
        AssociationId: Guid
        AppId: int
        Name: string
        ApplicationAccess: SaveApplicationAccessDTO
        Attributes: AttributeDTO list
    }

module SaveApplicationDTO = 
    let ofInitDTO (dto: InitApplicationDTO) = 
        {
            AssociationId = dto.AssociationId
            AppId = dto.AppId
            Name = dto.Name
            ApplicationAccess = SaveApplicationAccessDTO.ofInitDTO dto.ApplicationAccess
            Attributes = dto.Attributes
        }

type SaveDTO = 
    {
        UserId: int 
        Applications: SaveApplicationDTO list
    }

module SaveDTO = 
    let create userId applications = 
        {
            UserId = userId 
            Applications = applications
        }

module Validations = 
    open Illy.SafeStack.Validation
    open Illy.SafeStack.ROP
    open Illy.SafeStack.CompositionalValidation
    open Illy.SafeStack.SharedValidations

    type ManageApplicationsValidations = 
        | CannotEditDisabledUser
    
    module ManageApplicationsValidations = 
        let toString = 
            function 
            | CannotEditDisabledUser -> "Cannot modify applications for disabled users."
    
    let validateDTO isUserDisabled (dto: SaveDTO) = 
        validate {
            return! !!(error CannotEditDisabledUser)
                >>= conditional pass (fun _ -> not isUserDisabled)
                |> unwrap
        }

type FailureReason = 
    | NothingToDo
    | FailedValidations of Validation.ValidationResult<Validations.ManageApplicationsValidations, Validation.NoWarning>